import { RefObject, useLayoutEffect, useState } from 'react';

interface UseContainerSizeOptions {
  // When true, height changes that occur without a width change are ignored.
  // Use this on elements whose parent animates height (e.g. grid-template-rows collapse)
  // to avoid per-frame re-renders during the animation.
  widthOnly?: boolean;
}

export const useContainerSize = (
  ref: RefObject<HTMLElement | null>,
  { widthOnly = false }: UseContainerSizeOptions = {}
) => {
  const [size, setSize] = useState<{ width: number; height: number } | null>(null);

  useLayoutEffect(() => {
    const measure = () => {
      if (!ref.current) return;
      const { width, height } = ref.current.getBoundingClientRect();
      // Gating on width > 0 handles hidden tabs: size stays null until the tab
      // becomes visible and ResizeObserver fires with real dimensions.
      setSize(prev => {
        if (width === 0) return prev;
        if (widthOnly && prev?.width === width) return prev;
        if (!widthOnly && prev?.width === width && prev?.height === height) return prev;
        return { width, height };
      });
    };

    const ro = new ResizeObserver(measure);
    if (ref.current) {
      ro.observe(ref.current);
    }
    measure();

    return () => ro.disconnect();
  }, [ref, widthOnly]);

  return size;
};
